import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { 
  subscribeToRepairs, // Changed from getAllRepairs
  createRepair,
  updateRepairDetails,
  updateRepairStatus,
  markRepairAsPickedUp,
  getAllRepairs // Keep for summary
} from '../services/repairService';
import { RepairWithId, Repair, RepairStatus, PaymentStatus } from '../types';
import { auth, db } from '../services/firebase';
import { QueryDocumentSnapshot, Unsubscribe, getDoc, doc, Timestamp } from 'firebase/firestore';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

// Extended repair type that includes customer name and highlight flags
export interface RepairWithCustomer extends RepairWithId {
  customerName: string;
  _isNew?: boolean;
  _isUpdated?: boolean;
}

interface RepairSummaryData {
  activeRepairsCount: number;
}

interface RepairContextType {
  displayedRepairs: RepairWithCustomer[]; // Renamed from 'repairs'
  loading: boolean;
  error: string | null;
  selectedRepair: RepairWithCustomer | null;
  pagination: PaginationData;
  statusFilter: RepairStatus | 'all';
  repairSummary: RepairSummaryData | null;
  loadingRepairSummary: boolean;
  setStatusFilter: (filter: RepairStatus | 'all') => void;
  fetchNextPage: () => void;
  fetchPrevPage: () => void;
  fetchAndCalculateRepairSummary: () => Promise<void>;
  createNewRepair: (repair: { itemType: string; description: string; priceEstimate?: number; paymentStatus?: PaymentStatus; paidAmount?: number }, customerId: string) => Promise<string>;
  updateRepair: (repairId: string, updates: { status?: RepairStatus; description?: string; priceEstimate?: number | null; paymentStatus?: PaymentStatus; paidAmount?: number; remainingBalance?: number }) => Promise<void>;
  changeRepairStatus: (repairId: string, status: RepairStatus) => Promise<void>;
  markAsPickedUp: (repairId: string, finalPrice?: number) => Promise<void>;
  selectRepair: (repair: RepairWithCustomer | null) => void;
  dataLoaded: boolean;
  clearRepairHighlight: (repairId: string) => void;
}

const RepairContext = createContext<RepairContextType | undefined>(undefined);

export const useRepair = (): RepairContextType => {
  const context = useContext(RepairContext);
  if (context === undefined) {
    throw new Error('useRepair must be used within a RepairProvider');
  }
  return context;
};

interface RepairProviderProps {
  children: ReactNode;
}

const ITEMS_PER_PAGE = 10; // For client-side pagination
const SUBSCRIPTION_LIMIT = 50; // How many items to fetch with the listener

export const RepairProvider: React.FC<RepairProviderProps> = ({ children }) => {
  const [allSubscribedRepairs, setAllSubscribedRepairs] = useState<RepairWithCustomer[]>([]);
  const [displayedRepairs, setDisplayedRepairs] = useState<RepairWithCustomer[]>([]);
  const [selectedRepair, setSelectedRepair] = useState<RepairWithCustomer | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Start true until first data arrives
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<RepairStatus | 'all'>('all');
  const [repairSummary, setRepairSummary] = useState<RepairSummaryData | null>(null);
  const [loadingRepairSummary, setLoadingRepairSummary] = useState<boolean>(false);
  
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: ITEMS_PER_PAGE
  });

  const prevAllSubscribedRepairsRef = useRef<RepairWithCustomer[]>([]);

  useEffect(() => {
    prevAllSubscribedRepairsRef.current = allSubscribedRepairs;
  }, [allSubscribedRepairs]);

  // Effect for managing the real-time subscription
  useEffect(() => {
    setLoading(true);
    setDataLoaded(false);
    setError(null);

    const isAuthenticated = auth?.currentUser !== null;
    if (!isAuthenticated) {
      setError('Kullanıcı giriş yapmamış.');
      setLoading(false);
      setAllSubscribedRepairs([]);
      return;
    }

    // Track whether this is the initial data load
    let isInitialLoad = true;

    const unsubscribePromise = subscribeToRepairs(
      (fetchedRepairs) => {
        const prevRepairs = prevAllSubscribedRepairsRef.current;
        
        const updatedRepairs = fetchedRepairs.map(newRepair => {
          const oldRepair = prevRepairs.find(r => r.id === newRepair.id);
          let isNewFlag = false;
          let isUpdatedFlag = false;

          // Only mark items as new or updated if this is not the initial data load
          if (!isInitialLoad) {
            if (!oldRepair) {
              isNewFlag = true; // Item is new
            } else if (oldRepair.updatedAt && newRepair.updatedAt && 
                     oldRepair.updatedAt.toDate().getTime() < newRepair.updatedAt.toDate().getTime()) {
              isUpdatedFlag = true; // Item is updated
            }
          }
          
          return { ...newRepair, _isNew: isNewFlag, _isUpdated: isUpdatedFlag };
        });
        
        // Set this to false after the first data load
        isInitialLoad = false;
        
        setAllSubscribedRepairs(updatedRepairs);
        setLoading(false);
        setDataLoaded(true);
        setError(null);
      },
      statusFilter,
      SUBSCRIPTION_LIMIT // Fetch a batch for client-side pagination
    );

    let unsubscribe: Unsubscribe | null = null;
    unsubscribePromise.then(unsub => unsubscribe = unsub).catch(err => {
        console.error("Subscription error:", err);
        setError(err.message || 'Tamirat aboneliği kurulurken bir hata oluştu.');
        setLoading(false);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [statusFilter, auth?.currentUser]); // Re-subscribe if filter or user changes

  // Effect for client-side pagination
  useEffect(() => {
    const totalItems = allSubscribedRepairs.length;
    const totalPages = Math.ceil(totalItems / pagination.pageSize);
    
    setPagination(prev => ({
      ...prev,
      totalItems,
      totalPages: totalPages > 0 ? totalPages : 1,
      // Reset to page 1 if current page becomes invalid
      currentPage: prev.currentPage > totalPages && totalPages > 0 ? totalPages : (prev.currentPage === 0 && totalPages > 0 ? 1 : prev.currentPage)
    }));

    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    setDisplayedRepairs(allSubscribedRepairs.slice(startIndex, endIndex));

  }, [allSubscribedRepairs, pagination.currentPage, pagination.pageSize]);
  
  // Effect to reset pagination when filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [statusFilter]);


  const fetchNextPage = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.min(prev.currentPage + 1, prev.totalPages)
    }));
  };

  const fetchPrevPage = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: Math.max(prev.currentPage - 1, 1)
    }));
  };

  const clearRepairHighlight = (repairId: string) => {
    setAllSubscribedRepairs(prev => prev.map(r =>
      r.id === repairId ? { ...r, _isNew: false, _isUpdated: false } : r
    ));
  };

  const fetchAndCalculateRepairSummary = async (): Promise<void> => {
    setLoadingRepairSummary(true);
    setError(null);
    try {
      const isAuthenticated = auth?.currentUser !== null;
      if (!isAuthenticated) {
        setRepairSummary(null);
        throw new Error('Kullanıcı giriş yapmamış.');
      }
      
      // Using getAllRepairs for summary as it might need more data than subscribed for list view
      const { repairs: allRepairsForSummary } = await getAllRepairs(9999, 1, 'all', undefined);

      const activeRepairs = allRepairsForSummary.filter(
        (r) =>
          r.status === RepairStatus.Pending ||
          r.status === RepairStatus.InProgress ||
          r.status === RepairStatus.Done
      );
      setRepairSummary({ activeRepairsCount: activeRepairs.length });

    } catch (err: any) {
      console.error('Error fetching repair summary:', err);
      setError(err.message || 'Tamirat özeti alınırken bir hata oluştu.');
      setRepairSummary(null);
    } finally {
      setLoadingRepairSummary(false);
    }
  };

  const createNewRepair = async (
    repair: { itemType: string; description: string; priceEstimate?: number; paymentStatus?: PaymentStatus; paidAmount?: number },
    customerId: string
  ): Promise<string> => {
    // setLoading(true); // Listener will handle loading state changes for the list
    setError(null);
    try {
      const repairId = await createRepair(repair, customerId);
      // Listener should pick up the new repair. Optimistic update removed for now.
      // If needed, we can re-fetch summary or rely on listener for list update.
      fetchAndCalculateRepairSummary(); // Update summary
      return repairId;
    } catch (error) {
      setError('Tamirat eklenirken bir hata oluştu.');
      console.error('Error creating repair:', error);
      throw error;
    } finally {
      // setLoading(false);
    }
  };

  const updateRepair = async (
    repairId: string, 
    updates: { status?: RepairStatus; description?: string; priceEstimate?: number | null; paymentStatus?: PaymentStatus; paidAmount?: number; remainingBalance?: number }
  ): Promise<void> => {
    setError(null);
    try {
      await updateRepairDetails(repairId, updates);
      // Listener should pick up the changes.
      // Update selectedRepair if it's the one being edited.
      if (selectedRepair && selectedRepair.id === repairId) {
        // This local update is for the modal, listener updates the list.
        const currentSelected = selectedRepair;
            let updatedFields = { ...updates };
            if (updates.paidAmount !== undefined || updates.priceEstimate !== undefined) {
              const newPaidAmount = updates.paidAmount ?? currentSelected.paidAmount;
              const newPriceEstimate = updates.priceEstimate === null ? 0 : (updates.priceEstimate ?? currentSelected.priceEstimate ?? 0);
              let newPaymentStatus = updates.paymentStatus || PaymentStatus.Unpaid;
              if (newPaidAmount > 0 && newPriceEstimate > 0) {
                newPaymentStatus = newPaidAmount >= newPriceEstimate ? PaymentStatus.FullyPaid : PaymentStatus.PartiallyPaid;
              } else if (newPaidAmount === 0) newPaymentStatus = PaymentStatus.Unpaid;
              const newRemainingBalance = Math.max(0, newPriceEstimate - newPaidAmount);
              updatedFields = {...updatedFields, paymentStatus: newPaymentStatus, remainingBalance: newRemainingBalance};
            }
        setSelectedRepair(prev => prev ? {
            ...prev,
            ...updatedFields,
            priceEstimate: updates.priceEstimate === null ? undefined : updates.priceEstimate,
            updatedAt: Timestamp.now()
        } : null);
      }
      fetchAndCalculateRepairSummary(); // Update summary
    } catch (error) {
      setError('Tamirat güncellenirken bir hata oluştu.');
      console.error('Error updating repair:', error);
      throw error;
    }
  };

  const changeRepairStatus = async (repairId: string, status: RepairStatus): Promise<void> => {
    setError(null);
    try {
      await updateRepairStatus(repairId, status);
      // Listener should pick up the changes.
      if (selectedRepair && selectedRepair.id === repairId) {
        setSelectedRepair(prev => prev ? { ...prev, status, updatedAt: Timestamp.now() } : null);
      }
      fetchAndCalculateRepairSummary(); // Update summary
    } catch (error) {
      setError('Tamirat durumu güncellenirken bir hata oluştu.');
      console.error('Error updating repair status:', error);
      throw error;
    }
  };

  const markAsPickedUp = async (repairId: string, finalPrice?: number): Promise<void> => {
    setError(null);
    try {
      await markRepairAsPickedUp(repairId, finalPrice);
      // Listener should pick up the changes.
      if (selectedRepair && selectedRepair.id === repairId) {
        setSelectedRepair(prev => prev ? { 
          ...prev, 
          status: RepairStatus.PickedUp, 
          pickedUp: true,
          finalPrice,
          updatedAt: Timestamp.now() 
        } : null);
      }
      fetchAndCalculateRepairSummary(); // Update summary
    } catch (error) {
      setError('Tamirat teslim edilirken bir hata oluştu.');
      console.error('Error marking repair as picked up:', error);
      throw error;
    }
  };

  const selectRepair = (repair: RepairWithCustomer | null): void => {
    setSelectedRepair(repair);
  };

  return (
    <RepairContext.Provider
      value={{
        displayedRepairs,
        loading,
        error,
        selectedRepair,
        pagination,
        statusFilter,
        repairSummary,
        loadingRepairSummary,
        setStatusFilter,
        fetchNextPage,
        fetchPrevPage,
        fetchAndCalculateRepairSummary,
        createNewRepair,
        updateRepair,
        changeRepairStatus,
        markAsPickedUp,
        selectRepair,
        dataLoaded,
        clearRepairHighlight
      }}
    >
      {children}
    </RepairContext.Provider>
  );
}; 